import { Text, Box, ActionIcon, Popover, Divider, UnstyledButton, Modal } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import { useParams } from '@tanstack/react-router';
import { useConversationStore } from '../../../../stores/conversationStore';
import { useUserSettingsStore } from '../../../../stores/userSettingsStore';
import { useShallow } from 'zustand/react/shallow';
import { useIsMessageSending } from '../../../../hooks';
import { conversationUtils } from '../../../../api';
import { NAVBAR_FEATURES } from '../../../../config/featureFlags';
import { ConversationOptionsModal } from '../../AppShell/components/ConversationOptionsModal';
import { RenameModal } from '../../AppShell/components/RenameModal';
import { DeleteConfirmationModal } from '../../AppShell/components/DeleteConfirmationModal';
import { useState } from 'react';
import classes from './ConversationTitle.module.css';
import { useConversationDateLastAccessed } from '../../../../hooks/useConversationDateLastAccessed';
import { trackComponentInteraction } from '../../../../utils/analytics';


export interface ConversationTitleProps {
  className?: string;
}

export const ConversationTitle = ({ className }: ConversationTitleProps) => {
  // Use shouldThrow: false to prevent invariant exceptions when not on a conversation route
  const params = useParams({
    from: '/chat/$conversationId',
    shouldThrow: false
  });

  const { getConversationById, openModalConversationId, closeModal } = useConversationStore(useShallow((state) => ({
    getConversationById: state.getConversationById,
    openModalConversationId: state.openModalConversationId,
    closeModal: state.closeModal,
  })));

  const { isConversationPinned, togglePinnedConversation } = useUserSettingsStore();

  // UI state for popover/modals and hover
  const [isHovered, setIsHovered] = useState(false);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Check if any message is currently being sent across the entire app
  const isMessageSending = useIsMessageSending();

  // Return null if not on a conversation route or no conversationId
  if (!params?.conversationId) {
    return null;
  }

  const { updateDateLastAccessed } = useConversationDateLastAccessed(params.conversationId);


  const conversation = getConversationById(params.conversationId);

  if (!conversation || !conversation.conversationTitle) {
    return null;
  }

  const handleOptionsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setPopoverOpened((prev) => !prev);
  };

  const handlePopoverClose = () => setPopoverOpened(false);
  const handleMouseLeave = () => setIsHovered(false);

  const handleRename = () => {
    setIsRenameModalOpen(true);
    handlePopoverClose();
  };

  const handlePin = () => {
    updateDateLastAccessed();
    const isPinned = isConversationPinned(conversation.conversationID);
    trackComponentInteraction(
      'PinConversationTitle',
      conversation.conversationTitle,
      'conversation',
      isPinned ? 'unpin' : 'pin',
      isPinned ? 'conversation_unpinned' : 'conversation_pinned'
    );
    togglePinnedConversation(conversation.conversationID, conversation.conversationTitle);
  };

  const handleRenameSubmit = (_newTitle: string) => {
    setIsRenameModalOpen(false);
    setIsHovered(false);
  };

  const handleRenameCancel = () => {
    setIsRenameModalOpen(false);
    setIsHovered(false);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
    handlePopoverClose();
  };

  const handleDeleteConfirm = () => {
    setIsDeleteModalOpen(false);
    setIsHovered(false);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setIsHovered(false);
  };

  return (
    <>
      <Box className={`${classes.container} ${className || ''}`}>
        <div
          className={classes.titleContainer}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={handleMouseLeave}
        >
          <Text className={classes.title} lineClamp={1}>
            {conversation.conversationTitle}
          </Text>

          {/* Options popover */}
          <Popover
            width={240}
            position="right-start"
            shadow="0px 0px 12px 0px rgba(0, 0, 0, 0.24)"
            opened={popoverOpened}
            onChange={setPopoverOpened}
            onClose={handlePopoverClose}
            closeOnClickOutside
            closeOnEscape
            withinPortal
            styles={{
              dropdown: {
                border: '1px solid var(--fonterra-border-accent)',
                borderRadius: '6px',
                backgroundColor: 'var(--fonterra-surface-popover)',
                padding: '9px 0px',
                boxShadow: 'var(--fonterra-shadow-popover)',
              },
            }}
          >
            <Popover.Target>
              <div className={classes.optionsButtonWrapper}>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={handleOptionsClick}
                    disabled={isMessageSending}
                    className={classes.optionsButton}
                    aria-label="Conversation options"
                  >
                    <IconDots
                      size={16}
                      color={isMessageSending ? 'var(--fonterra-text-muted)' : 'var(--fonterra-text-primary)'}
                    />
                  </ActionIcon>
              </div>
            </Popover.Target>

            <Popover.Dropdown>
              <div className={classes.popoverContent}>
                <UnstyledButton onClick={handleRename} className={classes.popoverAction}>
                  <Text className={classes.actionText}>Rename</Text>
                </UnstyledButton>

                {NAVBAR_FEATURES.showPinnedChats && (
                  <UnstyledButton onClick={handlePin} className={classes.popoverAction}>
                    <Text className={classes.actionText}>
                      {isConversationPinned(conversation.conversationID) ? 'Unpin' : 'Pin'}
                    </Text>
                  </UnstyledButton>
                )}

                <div className={classes.dividerContainer}>
                  <Divider className={classes.divider} />
                </div>

                <UnstyledButton onClick={handleDelete} className={classes.popoverAction}>
                  <Text className={classes.actionText}>Delete</Text>
                </UnstyledButton>
              </div>
            </Popover.Dropdown>
          </Popover>
        </div>
      </Box>

      {/* Global Conversation Options Modal support (opened via store elsewhere) */}
      <ConversationOptionsModal
        conversationId={conversation.conversationID}
        opened={openModalConversationId === conversation.conversationID}
        onClose={closeModal}
        onRename={handleRename}
        onDelete={handleDelete}
      />

      {/* Rename Modal */}
      <Modal
        opened={isRenameModalOpen}
        onClose={handleRenameCancel}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          content: { backgroundColor: 'transparent' },
          body: { padding: 0 },
        }}
      >
        <RenameModal
          conversationId={conversation.conversationID}
          currentTitle={conversationUtils.getDisplayTitle(conversation, 200)}
          onRename={handleRenameSubmit}
          onCancel={handleRenameCancel}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          content: { backgroundColor: 'transparent' },
          body: { padding: 0 },
        }}
      >
        <DeleteConfirmationModal
          conversationId={conversation.conversationID}
          onDelete={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      </Modal>
    </>
  );
};
